import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, GitBranch, Send, Clock, Tag as TagIcon, ChevronDown, Play, Users, ArrowRight, Copy, PowerOff, Power } from "lucide-react";

const NODE_META = {
  send: { icon: Send, label: "Send message", color: "text-sky-700 border-sky-300 bg-sky-50 dark:bg-sky-900/20" },
  wait: { icon: Clock, label: "Wait", color: "text-amber-700 border-amber-300 bg-amber-50 dark:bg-amber-900/20" },
  branch: { icon: GitBranch, label: "Branch (if tag)", color: "text-fuchsia-700 border-fuchsia-300 bg-fuchsia-50 dark:bg-fuchsia-900/20" },
  tag: { icon: TagIcon, label: "Add/Remove tag", color: "text-emerald-700 border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20" },
  end: { icon: PowerOff, label: "End", color: "text-zinc-700 border-zinc-300" },
};

function newNodeOf(type) {
  const id = "n_" + Math.random().toString(36).slice(2, 8);
  const base = { id, type, next_id: null };
  if (type === "send") return { ...base, channel: "whatsapp", body: "Hi {{name}}! " };
  if (type === "wait") return { ...base, delay_seconds: 60 };
  if (type === "branch") return { ...base, branch_tag: "vip", on_true: null, on_false: null };
  if (type === "tag") return { ...base, tag_add: "engaged" };
  return base;
}

// ─────────── List page ───────────
export function JourneysList() {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tplOpen, setTplOpen] = useState(false);
  const nav = useNavigate();
  const load = () => api.get("/journeys").then(r => setItems(r.data));
  const loadTemplates = () => api.get("/journeys/templates/list").then(r => setTemplates(r.data));
  useEffect(() => { load(); loadTemplates(); }, []);

  const create = async () => {
    const first = newNodeOf("send");
    try {
      const { data } = await api.post("/journeys", {
        name: "New Journey", description: "",
        trigger: { type: "manual" }, is_active: false,
        entry_node_id: first.id, nodes: [first],
      });
      nav(`/journeys/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const createFromTemplate = async (key) => {
    try {
      const { data } = await api.post(`/journeys/templates/${key}`);
      toast.success(`Created "${data.name}"`);
      setTplOpen(false);
      nav(`/journeys/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id, e) => { e.stopPropagation(); if (!window.confirm("Delete this journey?")) return; await api.delete(`/journeys/${id}`); load(); };
  const toggle = async (j, e) => {
    e.stopPropagation();
    await api.patch(`/journeys/${j.id}`, {
      name: j.name, description: j.description || "", trigger: j.trigger,
      nodes: j.nodes, entry_node_id: j.entry_node_id, is_active: !j.is_active,
    });
    load();
  };

  return (
    <div className="space-y-4" data-testid="journeys-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Automation</div>
          <h1 className="text-3xl font-black tracking-tighter">Journeys</h1>
          <p className="text-xs text-muted-foreground mt-1">Multi-step, cross-channel automations — send, wait, branch on tags, apply tags.</p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={tplOpen} onOpenChange={setTplOpen}>
            <DialogTrigger asChild><Button variant="outline" className="rounded-sm gap-2" data-testid="from-template-button"><Copy className="h-4 w-4" /> From template</Button></DialogTrigger>
            <DialogContent className="rounded-sm max-w-lg">
              <DialogHeader><DialogTitle>Start from a template</DialogTitle></DialogHeader>
              <div className="space-y-2">
                {templates.map(t => (
                  <div key={t.key} className="border border-border rounded-sm p-3 hover:bg-accent/40 cursor-pointer" onClick={() => createFromTemplate(t.key)} data-testid={`template-${t.key}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-sm">{t.name}</div>
                      <Badge variant="outline" className="rounded-sm text-[10px]"><GitBranch className="h-3 w-3 inline mr-1" /> {t.node_count} nodes</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{t.description}</div>
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
          <Button className="rounded-sm gap-2" onClick={create} data-testid="new-journey-button"><Plus className="h-4 w-4" /> New Journey</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map(j => (
          <Card key={j.id} className="rounded-sm shadow-none cursor-pointer hover:bg-accent/40" onClick={() => nav(`/journeys/${j.id}`)} data-testid={`journey-${j.id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="font-semibold">{j.name}</div>
                {j.is_active
                  ? <Badge variant="outline" className="rounded-sm text-[10px] border-emerald-300 text-emerald-700 gap-1"><Power className="h-3 w-3" /> LIVE</Badge>
                  : <Badge variant="outline" className="rounded-sm text-[10px] border-zinc-300">DRAFT</Badge>}
              </div>
              <div className="text-xs text-muted-foreground">{j.description || "—"}</div>
              <div className="flex items-center gap-2 text-xs">
                <Badge variant="outline" className="rounded-sm text-[10px]"><GitBranch className="h-3 w-3 inline mr-1" /> {j.nodes?.length || 0} nodes</Badge>
                <Badge variant="outline" className="rounded-sm text-[10px]"><Users className="h-3 w-3 inline mr-1" /> {j.stats?.enrolled || 0} enrolled</Badge>
                <Badge variant="outline" className="rounded-sm text-[10px]">✓ {j.stats?.completed || 0} done</Badge>
              </div>
              <div className="flex items-center justify-between pt-1">
                <div className="flex items-center gap-1 text-xs">
                  <Switch checked={j.is_active} onCheckedChange={() => {}} onClick={(e) => toggle(j, e)} data-testid={`journey-toggle-${j.id}`} />
                  <span>{j.is_active ? "Live" : "Draft"}</span>
                </div>
                <Button variant="ghost" size="sm" className="rounded-sm text-red-600 h-7 w-7 p-0" onClick={(e) => del(j.id, e)}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <Card className="rounded-sm shadow-none md:col-span-3"><CardContent className="text-center text-muted-foreground py-10">No journeys yet.</CardContent></Card>}
      </div>
    </div>
  );
}

// ─────────── Editor ───────────
export function JourneyEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [j, setJ] = useState(null);
  const [runs, setRuns] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [enrolOpen, setEnrolOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState({});
  const [saving, setSaving] = useState(false);

  const refresh = () => {
    api.get(`/journeys/${id}`).then(r => setJ(r.data)).catch(() => toast.error("Not found"));
    api.get(`/journeys/${id}/runs`).then(r => setRuns(r.data)).catch(() => {});
    api.get(`/journeys/${id}/analytics`).then(r => setAnalytics(r.data)).catch(() => {});
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 8000);
    return () => clearInterval(t);
  }, [id]);

  const save = async () => {
    if (!j) return;
    setSaving(true);
    try {
      await api.patch(`/journeys/${id}`, {
        name: j.name, description: j.description || "", trigger: j.trigger,
        nodes: j.nodes, entry_node_id: j.entry_node_id, is_active: j.is_active,
      });
      toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const addNode = (type) => {
    const n = newNodeOf(type);
    setJ({ ...j, nodes: [...j.nodes, n] });
  };
  const removeNode = (nid) => {
    if (j.entry_node_id === nid) { toast.error("Cannot remove entry node"); return; }
    setJ({
      ...j,
      nodes: j.nodes.filter(n => n.id !== nid).map(n => ({
        ...n,
        next_id: n.next_id === nid ? null : n.next_id,
        on_true: n.on_true === nid ? null : n.on_true,
        on_false: n.on_false === nid ? null : n.on_false,
      })),
    });
  };
  const updateNode = (nid, patch) => setJ({ ...j, nodes: j.nodes.map(n => n.id === nid ? { ...n, ...patch } : n) });

  const openEnrol = async () => {
    setEnrolOpen(true);
    const { data } = await api.get("/contacts");
    setContacts(Array.isArray(data) ? data.slice(0, 50) : (data.items || []).slice(0, 50));
  };
  const doEnrol = async () => {
    const ids = Object.keys(selectedContacts).filter(k => selectedContacts[k]);
    if (ids.length === 0) return toast.error("Pick at least one contact");
    try { await api.post(`/journeys/${id}/enrol`, { contact_ids: ids }); toast.success(`Enrolled ${ids.length}`); setEnrolOpen(false); setSelectedContacts({}); }
    catch (e) { toast.error(e.response?.data?.detail || "Enrol failed"); }
  };

  if (!j) return <div className="text-sm text-muted-foreground p-4">Loading journey…</div>;

  return (
    <div className="space-y-4" data-testid="journey-editor">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Automation / Journey</div>
          <Input value={j.name} onChange={e => setJ({ ...j, name: e.target.value })} className="text-2xl font-black tracking-tight border-0 shadow-none px-0 focus-visible:ring-0 h-auto py-1" data-testid="journey-name" />
          <Input value={j.description || ""} onChange={e => setJ({ ...j, description: e.target.value })} placeholder="Description" className="border-0 shadow-none text-xs text-muted-foreground px-0 focus-visible:ring-0" />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 text-xs">
            <Switch checked={j.is_active} onCheckedChange={v => setJ({ ...j, is_active: v })} data-testid="journey-active" />
            <span>{j.is_active ? "Live" : "Draft"}</span>
          </div>
          <Button variant="outline" className="rounded-sm gap-2" onClick={openEnrol} data-testid="enrol-button"><Users className="h-4 w-4" /> Enrol contacts</Button>
          <Button className="rounded-sm gap-2" onClick={save} disabled={saving} data-testid="save-journey">Save</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          <Card className="rounded-sm shadow-none">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">Flow</div>
                <div className="flex gap-1">
                  {["send", "wait", "branch", "tag"].map(t => (
                    <Button key={t} variant="outline" size="sm" className="rounded-sm text-xs gap-1" onClick={() => addNode(t)} data-testid={`add-${t}`}>
                      <Plus className="h-3 w-3" /> {t}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                {j.nodes.map((n, idx) => {
                  const M = NODE_META[n.type] || NODE_META.end;
                  const Icon = M.icon;
                  const stat = analytics?.nodes?.find(s => s.node_id === n.id);
                  return (
                    <div key={n.id} className={`border rounded-sm p-3 ${M.color}`} data-testid={`node-${n.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Icon className="h-4 w-4" />
                          <span className="text-xs font-semibold uppercase tracking-wider">{M.label}</span>
                          <Badge variant="outline" className="rounded-sm text-[10px] bg-background/60">{n.id}</Badge>
                          {j.entry_node_id === n.id && <Badge variant="outline" className="rounded-sm text-[10px] border-emerald-400 text-emerald-700 bg-emerald-50">ENTRY</Badge>}
                          {stat && stat.reached > 0 && (
                            <>
                              <Badge variant="outline" className="rounded-sm text-[10px] font-mono bg-background/60" data-testid={`stat-reach-${n.id}`}>{stat.reached} reached</Badge>
                              {stat.drop_rate_pct > 0 && (
                                <Badge variant="outline" className={`rounded-sm text-[10px] font-mono ${stat.drop_rate_pct >= 30 ? "border-red-400 text-red-700 bg-red-50 dark:bg-red-900/20" : "border-amber-300 text-amber-700 bg-amber-50 dark:bg-amber-900/20"}`} data-testid={`stat-drop-${n.id}`}>-{stat.drop_rate_pct}% drop</Badge>
                              )}
                              {stat.avg_dwell_sec !== null && stat.avg_dwell_sec !== undefined && (
                                <Badge variant="outline" className="rounded-sm text-[10px] font-mono border-indigo-300 text-indigo-700 bg-indigo-50 dark:bg-indigo-900/20" data-testid={`stat-dwell-${n.id}`}>
                                  ⏱ {stat.avg_dwell_sec < 60 ? `${stat.avg_dwell_sec}s` : stat.avg_dwell_sec < 3600 ? `${Math.round(stat.avg_dwell_sec / 60)}m` : `${(stat.avg_dwell_sec / 3600).toFixed(1)}h`} avg
                                </Badge>
                              )}
                              {n.type === "send" && stat.deliveries > 0 && (
                                <Badge variant="outline" className="rounded-sm text-[10px] font-mono border-sky-300 text-sky-700 bg-sky-50" data-testid={`stat-deliv-${n.id}`}>📨 {stat.deliveries}</Badge>
                              )}
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" className="rounded-sm text-xs h-7" onClick={() => setJ({ ...j, entry_node_id: n.id })} data-testid={`set-entry-${n.id}`}>Set entry</Button>
                          <Button variant="ghost" size="sm" className="rounded-sm h-7 w-7 p-0 text-red-600" onClick={() => removeNode(n.id)} data-testid={`remove-${n.id}`}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      </div>
                      <div className="mt-2 space-y-2">
                        {n.type === "send" && (
                          <div className="grid grid-cols-2 gap-2">
                            <div><Label className="text-[10px]">Channel</Label>
                              <select value={n.channel} onChange={e => updateNode(n.id, { channel: e.target.value })} className="h-8 w-full px-2 rounded-sm border border-border bg-background text-xs" data-testid={`node-channel-${n.id}`}>
                                <option>whatsapp</option><option>sms</option><option>email</option><option>voice</option><option>slack</option>
                              </select>
                            </div>
                            <div><Label className="text-[10px]">Next node</Label>
                              <select value={n.next_id || ""} onChange={e => updateNode(n.id, { next_id: e.target.value || null })} className="h-8 w-full px-2 rounded-sm border border-border bg-background text-xs">
                                <option value="">— end —</option>
                                {j.nodes.filter(x => x.id !== n.id).map(x => <option key={x.id} value={x.id}>{x.id}</option>)}
                              </select>
                            </div>
                            <div className="col-span-2"><Label className="text-[10px]">Body</Label>
                              <textarea value={n.body || ""} onChange={e => updateNode(n.id, { body: e.target.value })} rows={2} className="w-full text-xs p-2 rounded-sm border border-border bg-background" data-testid={`node-body-${n.id}`} />
                            </div>
                          </div>
                        )}
                        {n.type === "wait" && (
                          <div className="grid grid-cols-2 gap-2">
                            <div><Label className="text-[10px]">Delay (seconds)</Label><Input type="number" value={n.delay_seconds || 0} onChange={e => updateNode(n.id, { delay_seconds: Number(e.target.value) })} className="rounded-sm h-8 text-xs" data-testid={`node-delay-${n.id}`} /></div>
                            <div><Label className="text-[10px]">Next node</Label>
                              <select value={n.next_id || ""} onChange={e => updateNode(n.id, { next_id: e.target.value || null })} className="h-8 w-full px-2 rounded-sm border border-border bg-background text-xs">
                                <option value="">— end —</option>
                                {j.nodes.filter(x => x.id !== n.id).map(x => <option key={x.id} value={x.id}>{x.id}</option>)}
                              </select>
                            </div>
                          </div>
                        )}
                        {n.type === "branch" && (
                          <div className="grid grid-cols-3 gap-2">
                            <div><Label className="text-[10px]">If tag present</Label><Input value={n.branch_tag || ""} onChange={e => updateNode(n.id, { branch_tag: e.target.value })} className="rounded-sm h-8 text-xs" placeholder="vip" data-testid={`node-tag-${n.id}`} /></div>
                            <div><Label className="text-[10px]">→ on TRUE</Label>
                              <select value={n.on_true || ""} onChange={e => updateNode(n.id, { on_true: e.target.value || null })} className="h-8 w-full px-2 rounded-sm border border-border bg-background text-xs">
                                <option value="">— end —</option>{j.nodes.filter(x => x.id !== n.id).map(x => <option key={x.id} value={x.id}>{x.id}</option>)}
                              </select>
                            </div>
                            <div><Label className="text-[10px]">→ on FALSE</Label>
                              <select value={n.on_false || ""} onChange={e => updateNode(n.id, { on_false: e.target.value || null })} className="h-8 w-full px-2 rounded-sm border border-border bg-background text-xs">
                                <option value="">— end —</option>{j.nodes.filter(x => x.id !== n.id).map(x => <option key={x.id} value={x.id}>{x.id}</option>)}
                              </select>
                            </div>
                          </div>
                        )}
                        {n.type === "tag" && (
                          <div className="grid grid-cols-3 gap-2">
                            <div><Label className="text-[10px]">Add tag</Label><Input value={n.tag_add || ""} onChange={e => updateNode(n.id, { tag_add: e.target.value })} className="rounded-sm h-8 text-xs" data-testid={`node-add-tag-${n.id}`} /></div>
                            <div><Label className="text-[10px]">Remove tag</Label><Input value={n.tag_remove || ""} onChange={e => updateNode(n.id, { tag_remove: e.target.value })} className="rounded-sm h-8 text-xs" /></div>
                            <div><Label className="text-[10px]">Next node</Label>
                              <select value={n.next_id || ""} onChange={e => updateNode(n.id, { next_id: e.target.value || null })} className="h-8 w-full px-2 rounded-sm border border-border bg-background text-xs">
                                <option value="">— end —</option>{j.nodes.filter(x => x.id !== n.id).map(x => <option key={x.id} value={x.id}>{x.id}</option>)}
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                      {idx < j.nodes.length - 1 && <div className="flex justify-center pt-2 text-muted-foreground"><ArrowRight className="h-3 w-3" /></div>}
                    </div>
                  );
                })}
                {j.nodes.length === 0 && <div className="text-center text-muted-foreground text-sm py-8">No nodes. Add one above.</div>}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-3">
          {analytics?.totals && (
            <Card className="rounded-sm shadow-none" data-testid="journey-analytics-panel">
              <CardContent className="p-4 space-y-2">
                <div className="text-sm font-semibold">Funnel</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="border border-border rounded-sm p-2 bg-muted/20"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Enrolled</div><div className="text-lg font-bold" data-testid="stat-enrolled">{analytics.totals.enrolled}</div></div>
                  <div className="border border-border rounded-sm p-2 bg-muted/20"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">In flight</div><div className="text-lg font-bold text-amber-700" data-testid="stat-inflight">{analytics.totals.in_flight}</div></div>
                  <div className="border border-border rounded-sm p-2 bg-muted/20"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Completed</div><div className="text-lg font-bold text-emerald-700" data-testid="stat-completed">{analytics.totals.completed}</div></div>
                  <div className="border border-border rounded-sm p-2 bg-muted/20"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Exited</div><div className="text-lg font-bold text-red-700" data-testid="stat-exited">{analytics.totals.exited}</div></div>
                </div>
                <div className="pt-1">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Completion rate</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-emerald-500 transition-all" style={{ width: `${analytics.totals.completion_rate_pct}%` }} />
                    </div>
                    <span className="text-xs font-mono font-bold" data-testid="stat-completion-rate">{analytics.totals.completion_rate_pct}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="rounded-sm shadow-none">
            <CardContent className="p-4 space-y-2">
              <div className="text-sm font-semibold">Runs ({runs.length})</div>
              <div className="max-h-[420px] overflow-auto text-xs space-y-1.5">
                {runs.map(r => (
                  <div key={r.id} className="border border-border rounded-sm p-2" data-testid={`run-${r.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono">{r.contact_id.slice(0, 10)}…</span>
                      <Badge variant="outline" className={`rounded-sm text-[10px] ${r.status === "completed" ? "border-emerald-300 text-emerald-700" : r.status === "queued" ? "border-amber-300 text-amber-700" : "border-zinc-300"}`}>{r.status}</Badge>
                    </div>
                    <div className="text-muted-foreground mt-1">At node: <code className="font-mono">{r.current_node_id || "—"}</code></div>
                    {r.trail?.length > 0 && <div className="text-[10px] text-muted-foreground truncate mt-1">Trail: {r.trail.map(t => t.type).join(" → ")}</div>}
                  </div>
                ))}
                {runs.length === 0 && <div className="text-center text-muted-foreground py-8">No enrolments yet.</div>}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={enrolOpen} onOpenChange={setEnrolOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle>Enrol contacts</DialogTitle></DialogHeader>
          <div className="max-h-80 overflow-auto border border-border rounded-sm">
            {contacts.map(c => (
              <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 border-b border-border last:border-b-0 text-xs cursor-pointer">
                <input type="checkbox" checked={!!selectedContacts[c.id]} onChange={e => setSelectedContacts({ ...selectedContacts, [c.id]: e.target.checked })} data-testid={`enrol-cb-${c.id}`} />
                <span className="font-medium">{c.name}</span>
                <span className="font-mono text-muted-foreground">{c.phone}</span>
              </label>
            ))}
          </div>
          <DialogFooter><Button className="rounded-sm gap-2" onClick={doEnrol} data-testid="do-enrol"><Play className="h-3 w-3" /> Enrol {Object.values(selectedContacts).filter(Boolean).length}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
